









































































































































































































import { Component, Vue } from "vue-property-decorator";
import moment from "moment";
import Referensi from "@/services/Referensi";
import LaporanBarberJohnson, {
  BarberJohnsonResponse,
  BjIndikatorItem,
  BjStatus
} from "@/services/LaporanBarberJohnson";
import BarberJohnsonChart from "@/components/chart/BarberJohnsonChart.vue";
import VCDateTimePicker from "@/components/form_components/VCDateTimePicker.vue";

interface UnitOption {
  id: string;
  nama: string;
}

interface PresetPeriode {
  key: string;
  label: string;
  mulai: () => string;
  selesai: () => string;
}

const FORMAT = "YYYY-MM-DD";

@Component({
  components: { BarberJohnsonChart, "v-c-date-time-picker": VCDateTimePicker }
})
export default class LaporanBarberJohnsonPage extends Vue {
  // Disimpan sebagai string YYYY-MM-DD; konversi ke Date hanya lewat computed di
  // bawah supaya objek Date tidak pernah masuk state (toISOString menggeser hari).
  mulai = moment().startOf("year").format(FORMAT);
  selesai = moment().format(FORMAT);
  hariIni = moment().format(FORMAT);

  selectedUnits: string[] = [];
  unitOptions: UnitOption[] = [];
  loadingUnits = false;

  loading = false;
  printing = false;
  data: BarberJohnsonResponse | null = null;

  readonly kartuIndikator = [
    { key: "bor", kode: "BOR", nama: "Bed Occupancy Rate", satuan: "%" },
    { key: "alos", kode: "ALOS", nama: "Average Length of Stay", satuan: "hari" },
    { key: "toi", kode: "TOI", nama: "Turn Over Interval", satuan: "hari" },
    { key: "bto", kode: "BTO", nama: "Bed Turn Over (setahun)", satuan: "kali" }
  ];

  readonly presets: PresetPeriode[] = [
    {
      key: "tahun-ini",
      label: "Tahun Ini",
      mulai: () => moment().startOf("year").format(FORMAT),
      selesai: () => moment().format(FORMAT)
    },
    {
      key: "tahun-lalu",
      label: "Tahun Lalu",
      mulai: () => moment().subtract(1, "year").startOf("year").format(FORMAT),
      selesai: () => moment().subtract(1, "year").endOf("year").format(FORMAT)
    },
    {
      key: "12-bulan",
      label: "12 Bulan Terakhir",
      mulai: () => moment().subtract(11, "month").startOf("month").format(FORMAT),
      selesai: () => moment().format(FORMAT)
    },
    {
      key: "bulan-ini",
      label: "Bulan Ini",
      mulai: () => moment().startOf("month").format(FORMAT),
      selesai: () => moment().format(FORMAT)
    }
  ];

  /** v-calendar meng-emit objek Date; state tetap string lokal.
   * Tipe getter & setter wajib sama persis (TS 3.8), sedangkan nilai yang benar-benar
   * tiba saat runtime bisa berupa string — itulah yang ditangani `keTanggalLokal()`. */
  get tglMulai(): Date {
    return moment(this.mulai, FORMAT).toDate();
  }
  set tglMulai(val: Date) {
    this.mulai = this.keTanggalLokal(val, this.mulai);
  }

  get tglSelesai(): Date {
    return moment(this.selesai, FORMAT).toDate();
  }
  set tglSelesai(val: Date) {
    this.selesai = this.keTanggalLokal(val, this.selesai);
  }

  /** Selalu memformat sebagai waktu LOKAL — `toISOString()` memundurkan tanggal
   * satu hari untuk tengah malam Jakarta. */
  private keTanggalLokal(val: Date | string | null, cadangan: string): string {
    if (val === null || val === undefined || val === "") return cadangan;
    const m = typeof val === "string" ? moment(val, [FORMAT, moment.ISO_8601]) : moment(val);
    return m.isValid() ? m.format(FORMAT) : cadangan;
  }

  get canSearch(): boolean {
    return (
      !!this.mulai &&
      !!this.selesai &&
      moment(this.selesai, FORMAT).isSameOrAfter(moment(this.mulai, FORMAT))
    );
  }

  /** vue-chartjs memasang `styles` pada div pembungkus canvas — di situlah tinggi
   * harus diberikan, karena maintainAspectRatio:false membuat Chart.js mengikuti
   * ukuran pembungkus langsungnya, bukan elemen leluhur. */
  get gayaGrafik(): Record<string, string> {
    return { position: "relative", height: "480px" };
  }

  mounted() {
    this.loadUnits();
  }

  indikator(key: string): BjIndikatorItem {
    if (!this.data) return { nilai: null, ideal: "-", status: null };
    return this.data.analisis.indikator[key];
  }

  formatAngka(v: number | null): string {
    if (v === null || v === undefined) return "-";
    return v.toLocaleString("id-ID", {
      minimumFractionDigits: 2,
      maximumFractionDigits: 2
    });
  }

  labelStatus(s: BjStatus): string {
    if (s === "ideal") return "IDEAL";
    if (s === "di_bawah") return "DI BAWAH IDEAL";
    if (s === "di_atas") return "DI ATAS IDEAL";
    return "-";
  }

  warnaStatus(s: BjStatus): string {
    if (s === "ideal") return "green";
    if (s === "di_bawah" || s === "di_atas") return "red darken-1";
    return "grey";
  }

  pakaiPreset(p: PresetPeriode) {
    this.mulai = p.mulai();
    this.selesai = p.selesai();
  }

  goMenu() {
    this.$router.push({ name: "laporan-rp1-menu" }).catch(() => undefined);
  }

  async loadUnits() {
    this.loadingUnits = true;
    try {
      const data = await Referensi.getUnitPelayanan();
      this.unitOptions = (data ?? [])
        .map((u: { id: string | number; nama: string }) => ({
          id: String(u.id),
          nama: u.nama
        }))
        .filter((u: UnitOption) => u.id >= "50" && u.id < "80")
        .sort((a: UnitOption, b: UnitOption) => a.id.localeCompare(b.id));
    } catch {
      this.unitOptions = [];
    } finally {
      this.loadingUnits = false;
    }
  }

  async tampilkan() {
    this.loading = true;
    try {
      this.data = await LaporanBarberJohnson.preview({
        periode_start: this.mulai,
        periode_end: this.selesai,
        unit: this.selectedUnits
      });
    } finally {
      this.loading = false;
    }
  }

  async cetak() {
    this.printing = true;
    try {
      // Grafik dikirim sebagai PNG kanvas — DomPDF tidak bisa menggambar Chart.js.
      const chart = (this.$refs.grafik as unknown) as
        | { ambilGambar: () => string | null }
        | undefined;
      const gambar = chart ? chart.ambilGambar() : null;

      await LaporanBarberJohnson.openPdf(
        {
          periode_start: this.mulai,
          periode_end: this.selesai,
          unit: this.selectedUnits
        },
        gambar
      );
    } finally {
      this.printing = false;
    }
  }
}
